import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, Star, Check, Sparkles, Shield, Truck, Heart, Info } from "lucide-react";
import { Navbar } from "@/components/sections/Navbar";
import { Footer } from "@/components/sections/Footer";
import { SubtleWaves } from "@/components/ui/waves";
import { useCart } from "@/context/CartContext";
import { StickyCartButton } from "@/components/StickyCartButton";
import { CartDrawer } from "@/components/CartDrawer";
import { Product } from "@/data/products";
import { ProductInfoModal } from "@/components/ProductInfoModal";
import { useProducts } from "@/hooks/useProducts";
import productPackage from "@/assets/product-package.jpeg";
import teapotInfusion from "@/assets/lunssence-teapot-hand.jpg";
import lifestyleWarmIngredients from "@/assets/lifestyle-warm-ingredients.jpg";
import goldenScoopCloseup from "@/assets/golden-scoop-closeup.jpg";
import pouringTeaRitual from "@/assets/pouring-tea-ritual.jpg";

// Helper to convert price strings to numbers
const parsePrice = (value: string): number => {
  return Number(value.replace('€', '').replace(',', '.').trim());
};

export const ProductenPage = () => {
  const [hoveredProduct, setHoveredProduct] = useState<string | null>(null);
  const [selectedSizes, setSelectedSizes] = useState<Record<string, '25g' | '50g'>>({});
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { addItem } = useCart();
  const { products, isLoading } = useProducts();

  const openProductInfo = (product: Product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const closeProductInfo = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedProduct(null), 300);
  };

  if (isLoading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold mx-auto mb-4"></div>
            <p className="text-muted-foreground">Producten laden...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Filter producten per categorie op slug
  const teaBlends = products.filter(p =>
    !p.isAccessory && !['blauwe-lotus', 'gele-lotus'].includes(p.slug)
  );
  const pureLotus = products.filter(p => 
    ['blauwe-lotus', 'gele-lotus'].includes(p.slug)
  );
  const accessories = products.filter(p => p.isAccessory);

  const bundle = {
    id: 3,
    name: "Complete Ritueel Collectie",
    description: "Alle 3 blends voor jouw perfecte ritueel",
    price: "€29,95",
    originalPrice: "€33,33",
    savings: "€3,38",
    features: ["Alle 3 unieke blends", "Luxe geschenkverpakking", "Perfect cadeau", "15% korting"],
    badge: "Beste deal",
    badgeColor: "bg-destructive",
  };

  const trustIcons = [
    { icon: Truck, text: "Gratis verzending vanaf €50" },
    { icon: Sparkles, text: "100% Natuurlijk" },
    { icon: Shield, text: "Premium kwaliteit gecertificeerd" },
    { icon: Heart, text: "Met liefde gemaakt" },
  ];

  const socialPosts = [
    { id: 1, image: teapotInfusion, caption: "Bloeiende rituelen", likes: 342 },
    { id: 2, image: lifestyleWarmIngredients, caption: "Natuurlijke schoonheid", likes: 289 },
    { id: 3, image: goldenScoopCloseup, caption: "Gouden momenten", likes: 421 },
    { id: 4, image: pouringTeaRitual, caption: "Pure ontspanning", likes: 367 },
  ];

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <SubtleWaves />
        <div className="absolute inset-0 bg-gradient-lavender opacity-30" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-serif text-4xl md:text-6xl font-bold text-foreground mb-6 animate-fade-in-up text-glow">
              Onze Collectie
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed animate-fade-in-up delay-200">
              Drie unieke blends, twee formaten, één perfecte ritueel voor jou
            </p>
          </div>
        </div>
      </section>

      {/* Blends Section */}
      <section className="relative py-24 bg-gradient-to-b from-background to-emerald-50/30">
        <div className="container mx-auto px-4">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-gold text-glow text-center mb-16">
            Kies jouw Blend
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-20">
            {teaBlends.map((blend, index) => (
              <div
                key={blend.slug}
                className={`group relative bg-card rounded-2xl ${blend.borderColor} border-2 p-8 transition-all duration-300 hover:scale-105 ${
                  hoveredProduct === blend.slug ? blend.glowColor : 'shadow-lg'
                }`}
                onMouseEnter={() => setHoveredProduct(blend.slug)}
                onMouseLeave={() => setHoveredProduct(null)}
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className={`absolute top-0 right-0 w-24 h-24 ${blend.accentColor} rounded-2xl opacity-20 transform rotate-12 group-hover:rotate-6 transition-transform duration-500`} />
                
                <div className="relative mb-6 overflow-hidden rounded-xl">
                  <img
                    src={blend.productImage}
                    alt={blend.name}
                    className="w-full h-64 object-cover rounded-xl shadow-md group-hover:scale-105 transition-all duration-300"
                    onError={(e) => {
                      e.currentTarget.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Crect fill='%23f0f0f0' width='400' height='400'/%3E%3Ctext fill='%23999' font-family='sans-serif' font-size='24' x='50%25' y='50%25' text-anchor='middle' dominant-baseline='middle'%3EGeen afbeelding%3C/text%3E%3C/svg%3E";
                    }}
                  />
                </div>

                <h3 className="font-serif text-2xl font-bold text-foreground mb-3">{blend.name}</h3>
                <p className="text-lg font-light italic text-gold/80 mb-4">{blend.tagline}</p>
                <p className="text-sm text-muted-foreground leading-relaxed mb-6">{blend.description}</p>

                {/* Size Selector */}
                <div className="mb-6 space-y-3">
                  <div 
                    onClick={() => setSelectedSizes({...selectedSizes, [blend.slug]: '25g'})}
                    className={`flex justify-between items-center p-4 md:p-5 min-h-[64px] touch-manipulation border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                      (selectedSizes[blend.slug] || '25g') === '25g' 
                        ? 'border-gold bg-gold/10 shadow-md' 
                        : 'border-muted hover:border-gold/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                        (selectedSizes[blend.slug] || '25g') === '25g' ? 'border-gold' : 'border-muted'
                      }`}>
                        {(selectedSizes[blend.slug] || '25g') === '25g' && (
                          <div className="w-4 h-4 md:w-3 md:h-3 rounded-full bg-gold" />
                        )}
                      </div>
                      <span className="font-medium text-foreground text-base md:text-base">25g</span>
                    </div>
                    <span className="text-lg md:text-lg font-bold text-gold">{blend.price25g}</span>
                  </div>
                  
                  <div 
                    onClick={() => setSelectedSizes({...selectedSizes, [blend.slug]: '50g'})}
                    className={`flex justify-between items-center p-4 md:p-5 min-h-[64px] touch-manipulation border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                      selectedSizes[blend.slug] === '50g' 
                        ? 'border-gold bg-gold/10 shadow-md' 
                        : 'border-muted hover:border-gold/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                        selectedSizes[blend.slug] === '50g' ? 'border-gold' : 'border-muted'
                      }`}>
                        {selectedSizes[blend.slug] === '50g' && (
                          <div className="w-4 h-4 md:w-3 md:h-3 rounded-full bg-gold" />
                        )}
                      </div>
                      <span className="font-medium text-foreground text-base md:text-base">50g</span>
                    </div>
                    <span className="text-lg md:text-lg font-bold text-gold">{blend.price50g}</span>
                  </div>
                </div>

                {/* Info & Cart Buttons */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => openProductInfo(blend)}
                    className="flex-1 border-gold/30 text-gold hover:bg-gold/10 hover:border-gold transition-all duration-200"
                  >
                    <Info className="w-4 h-4 mr-2" />
                    Meer info
                  </Button>

                  <Button
                    onClick={() => {
                      const size = selectedSizes[blend.slug] || '25g';
                      addItem({
                        id: `${blend.slug}-${size}`,
                        name: `${blend.name} - ${size}`,
                        size: size,
                        price: size === '25g' ? parsePrice(blend.price25g) : parsePrice(blend.price50g),
                        productId: blend.dbId,
                        image: blend.productImage
                      });
                    }}
                    className="btn-hero flex-[2] group"
                  >
                    <ShoppingCart className="w-4 h-4 mr-2 group-hover:animate-bounce" />
                    Voeg {selectedSizes[blend.slug] || '25g'} toe
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pure Lotus Section */}
      <section className="relative py-24 bg-gradient-to-b from-emerald-50/30 to-purple-50/30">
        <div className="container mx-auto px-4">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-gold text-glow text-center mb-4">
            Pure Lotus Collectie
          </h2>
          <p className="text-center text-muted-foreground mb-16 max-w-2xl mx-auto">
            Ervaar de pure kracht van heilige lotus bloemen - precies zoals de oude Egyptenaren het gebruikten
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {pureLotus.map((product, index) => (
              <div
                key={product.slug}
                className={`group relative bg-card rounded-2xl ${product.borderColor} border-2 p-8 transition-all duration-300 hover:scale-105 ${
                  hoveredProduct === product.slug ? product.glowColor : 'shadow-lg'
                }`}
                onMouseEnter={() => setHoveredProduct(product.slug)}
                onMouseLeave={() => setHoveredProduct(null)}
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className={`absolute top-0 right-0 w-24 h-24 ${product.accentColor} rounded-2xl opacity-20 transform rotate-12 group-hover:rotate-6 transition-transform duration-500`} />
                
                <div className="relative mb-6 overflow-hidden rounded-xl">
                  <img
                    src={product.productImage}
                    alt={product.name}
                    className="w-full h-80 object-cover rounded-xl shadow-md group-hover:scale-105 transition-all duration-300"
                  />
                </div>

                <h3 className="font-serif text-2xl font-bold text-foreground mb-3">{product.name}</h3>
                <p className="text-lg font-light italic text-gold/80 mb-4">{product.tagline}</p>
                <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                  {product.longDescription.split('\n\n')[0]}
                </p>

                {/* Size Selector */}
                <div className="mb-6 space-y-3">
                  <div 
                    onClick={() => setSelectedSizes({...selectedSizes, [product.slug]: '25g'})}
                    className={`flex justify-between items-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                      (selectedSizes[product.slug] || '25g') === '25g' 
                        ? 'border-gold bg-gold/10 shadow-md' 
                        : 'border-muted hover:border-gold/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                        (selectedSizes[product.slug] || '25g') === '25g' ? 'border-gold' : 'border-muted'
                      }`}>
                        {(selectedSizes[product.slug] || '25g') === '25g' && (
                          <div className="w-3 h-3 rounded-full bg-gold" />
                        )}
                      </div>
                      <span className="font-medium text-foreground">25g</span>
                    </div>
                    <span className="text-lg font-bold text-gold">{product.price25g}</span>
                  </div>
                  
                  <div 
                    onClick={() => setSelectedSizes({...selectedSizes, [product.slug]: '50g'})}
                    className={`flex justify-between items-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                      selectedSizes[product.slug] === '50g' 
                        ? 'border-gold bg-gold/10 shadow-md' 
                        : 'border-muted hover:border-gold/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                        selectedSizes[product.slug] === '50g' ? 'border-gold' : 'border-muted'
                      }`}>
                        {selectedSizes[product.slug] === '50g' && (
                          <div className="w-3 h-3 rounded-full bg-gold" />
                        )}
                      </div>
                      <span className="font-medium text-foreground">50g</span>
                    </div>
                    <span className="text-lg font-bold text-gold">{product.price50g}</span>
                  </div>
                </div>

                {/* Info & Cart Buttons */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => openProductInfo(product)}
                    className="flex-1 border-gold/30 text-gold hover:bg-gold/10 hover:border-gold transition-all duration-200"
                  >
                    <Info className="w-4 h-4 mr-2" />
                    Meer info
                  </Button>

                  <Button 
                    onClick={() => {
                      const size = selectedSizes[product.slug] || '25g';
                      addItem({
                        id: `${product.slug}-${size}`,
                        name: `${product.name} - ${size}`,
                        size: size,
                        price: size === '25g' ? parsePrice(product.price25g) : parsePrice(product.price50g),
                        productId: product.dbId,
                        image: product.productImage
                      });
                    }}
                    className="btn-hero flex-[2] group"
                  >
                    <ShoppingCart className="w-4 h-4 mr-2 group-hover:animate-bounce" />
                    Voeg {selectedSizes[product.slug] || '25g'} toe
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Accessories Section - Thee Pot */}
      <section className="relative py-24 bg-gradient-to-b from-purple-50/30 to-background">
        <div className="container mx-auto px-4">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-gold text-glow text-center mb-16">
            Ritueel Accessoires
          </h2>
          
          <div className="max-w-2xl mx-auto">
            {accessories.map((product) => (
              <div
                key={product.slug}
                className="relative bg-card rounded-2xl border-2 border-amber-300 p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-102"
              >
                {/* SALE BADGE */}
                <div className="absolute -top-4 -right-4 z-10">
                  <Badge className="bg-destructive text-white px-6 py-3 text-lg font-bold shadow-lg rotate-12 animate-pulse">
                    🔥 SALE -20%
                  </Badge>
                </div>
                
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  {/* Afbeelding */}
                  <div className="relative">
                    <img
                      src={teapotInfusion}
                      alt={product.name}
                      className="w-full h-80 object-cover rounded-xl shadow-md"
                    />
                  </div>
                  
                  {/* Content */}
                  <div>
                    <h3 className="font-serif text-3xl font-bold text-foreground mb-3">
                      {product.name}
                    </h3>
                    <p className="text-lg font-light italic text-gold/80 mb-4">
                      {product.tagline}
                    </p>
                    <p className="text-sm text-muted-foreground mb-6">
                      {product.description}
                    </p>
                    
                    {/* Pricing */}
                    <div className="flex items-center gap-4 mb-6">
                      <span className="text-4xl font-bold text-gold">{product.price25g}</span>
                      <span className="text-2xl text-muted-foreground line-through">{product.originalPrice}</span>
                      <Badge className="bg-destructive text-white">-€4,45</Badge>
                    </div>
                    
                    {/* Features */}
                    <ul className="space-y-2 mb-6">
                      {product.bullets.slice(0, 3).map((bullet, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-gold flex-shrink-0" />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                    
                    {/* CTA */}
                    <Button 
                      onClick={() => addItem({
                        id: `${product.slug}-600ml`,
                        name: product.name,
                        size: "600ml",
                        price: parsePrice(product.price25g),
                        productId: product.dbId,
                        image: product.productImage
                      })}
                      className="btn-hero w-full group"
                    >
                      <ShoppingCart className="w-5 h-5 mr-2 group-hover:animate-bounce" />
                      Voeg toe - €17,77
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bundle Section */}
      <section className="relative py-24 bg-primary/90">
        <SubtleWaves className="opacity-20" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto bg-background/95 backdrop-blur-md rounded-3xl p-6 md:p-12 border-2 border-gold shadow-floating-intense">
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
              <Badge className={`${bundle.badgeColor} text-white px-6 md:px-8 py-2 md:py-3 text-base md:text-lg font-bold shadow-gold`}>
                {bundle.badge}
              </Badge>
            </div>

            <div className="text-center mb-8">
              <h3 className="font-serif text-2xl md:text-4xl font-bold text-gold mb-4 text-glow">{bundle.name}</h3>
              <p className="text-base md:text-xl text-muted-foreground mb-6">{bundle.description}</p>

              <div className="flex items-center justify-center gap-3 md:gap-6 mb-8 overflow-x-auto">
                {teaBlends.slice(0, 3).map((blend, index) => (
                  <div key={index} className="relative flex-shrink-0">
                    <img
                      src={blend.productImage}
                      alt={blend.name}
                      className="w-16 h-20 md:w-20 md:h-24 object-cover rounded-lg shadow-md"
                      onError={(e) => {
                        e.currentTarget.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Crect fill='%23f0f0f0' width='400' height='400'/%3E%3Ctext fill='%23999' font-family='sans-serif' font-size='24' x='50%25' y='50%25' text-anchor='middle' dominant-baseline='middle'%3EGeen afbeelding%3C/text%3E%3C/svg%3E";
                      }}
                    />
                    {index < 2 && (
                      <span className="absolute -right-1.5 md:-right-3 top-1/2 -translate-y-1/2 text-lg md:text-2xl font-bold text-primary">
                        +
                      </span>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-center gap-3 md:gap-4 mb-8">
                <span className="text-3xl md:text-4xl font-bold text-gold">{bundle.price}</span>
                <span className="text-lg md:text-xl text-muted-foreground line-through">{bundle.originalPrice}</span>
                <Badge className="bg-destructive text-white px-3 md:px-4 py-1.5 md:py-2 text-sm md:text-lg">Bespaar {bundle.savings}</Badge>
              </div>

              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-8 text-left md:text-center">
                {bundle.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm md:text-base text-foreground">
                    <Check className="w-4 h-4 md:w-5 md:h-5 text-gold flex-shrink-0" />
                    <span className="leading-snug">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button 
                onClick={() => addItem({
                  id: "bundle-complete",
                  name: "Complete Ritueel Collectie",
                  size: "3x 25g",
                  price: parsePrice(bundle.price),
                  image: teaBlends[0]?.productImage
                })}
                className="btn-hero text-base md:text-xl px-6 py-4 md:px-12 md:py-6 w-full md:w-auto"
              >
                <ShoppingCart className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                <span className="hidden md:inline">Creëer jouw complete ritueel</span>
                <span className="md:hidden">Bestel Complete Set</span>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Icons */}
      <section className="py-12 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {trustIcons.map((item, idx) => {
              const IconComponent = item.icon;
              return (
                <div key={idx} className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center mb-4">
                    <IconComponent className="w-8 h-8 text-gold" />
                  </div>
                  <p className="text-sm text-muted-foreground">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* #LunssenceLife Grid */}
      <section className="py-20 bg-gradient-to-b from-background to-gold/5">
        <div className="container mx-auto px-4">
          <h3 className="font-serif text-3xl md:text-4xl font-bold text-gold text-glow text-center mb-12">
            #LunssenceLife
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {socialPosts.map((post) => (
              <div
                key={post.id}
                className="group relative aspect-square overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <img
                  src={post.image}
                  alt={post.caption}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="text-white text-sm mb-1">{post.caption}</p>
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-gold fill-gold" />
                      <span className="text-white text-xs">{post.likes} likes</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <p className="text-muted-foreground">
              Tag ons <span className="text-gold font-semibold">@lunssence_official</span> en gebruik
              <span className="text-gold font-semibold"> #LunssenceLife</span>
            </p>
          </div>
        </div>
      </section>

      <StickyCartButton />
      <CartDrawer />

      {/* Product Info Modal */}
      {selectedProduct && (
        <ProductInfoModal
          product={selectedProduct}
          isOpen={isModalOpen}
          onClose={closeProductInfo}
        />
      )}

      <Footer />
    </>
  );
};